import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  MessageCircle,
  Bot,
  User,
  Clock,
  CheckCircle2,
  Send,
  RefreshCw,
  Search,
  Bug,
  Lightbulb,
  HelpCircle,
  AlertTriangle,
  Zap,
  ArrowRight,
  Copy,
  Flame,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Loader2,
  BookOpen,
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
} from "lucide-react";

// Lucide icons that can be referenced from the products registry. Anything we
// don't whitelist falls back to <Box />.
const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };

// Tailwind color families → chip styles. Stays in sync with SupportChat
// widget and DevBacklogPage.
const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const CATEGORY_CONFIG = {
  question: { label: "Question", icon: HelpCircle, color: "text-blue-400 border-blue-500/50 bg-blue-500/10" },
  bug: { label: "Bug", icon: Bug, color: "text-red-400 border-red-500/50 bg-red-500/10" },
  feature: { label: "Feature", icon: Lightbulb, color: "text-green-400 border-green-500/50 bg-green-500/10" },
  complaint: { label: "Complaint", icon: AlertTriangle, color: "text-yellow-400 border-yellow-500/50 bg-yellow-500/10" },
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "text-red-400 border-red-500/50 bg-red-500/10", icon: Flame },
  high: { label: "High", color: "text-orange-400 border-orange-500/50 bg-orange-500/10", icon: ChevronUp },
  normal: { label: "Normal", color: "text-blue-400 border-blue-500/50 bg-blue-500/10", icon: ArrowRight },
  low: { label: "Low", color: "text-zinc-400 border-zinc-500/50 bg-zinc-500/10", icon: ChevronDown },
};

const STATUS_CONFIG = {
  open: { label: "Open", color: "text-orange-400 border-orange-500/50 bg-orange-500/10" },
  in_progress: { label: "In Progress", color: "text-blue-400 border-blue-500/50 bg-blue-500/10" },
  resolved: { label: "Resolved", color: "text-green-400 border-green-500/50 bg-green-500/10" },
  escalated: { label: "Escalated", color: "text-red-400 border-red-500/50 bg-red-500/10" },
};

const TABS = [
  { key: "active", label: "Active", statuses: ["open", "escalated", "in_progress"] },
  { key: "resolved", label: "Resolved", statuses: ["resolved"] },
  { key: "all", label: "All", statuses: null },
];

const SupportChatsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  // Per-instance filter — when filterProduct narrows to a multi-tenant
  // product (Spinhouse/Vexos), this picks which deployment to focus on
  // (Ola, Ocean Cove, …). "all" means show all instances of the product.
  const [filterInstance, setFilterInstance] = useState("all");
  // Source filter — partner submissions vs internal HUB chats. Mirrors the
  // backend `source` query param. Splits the queue so PM/dev viewing
  // partner feedback doesn't drown in admin-only HUB threads.
  const [filterSource, setFilterSource] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [products, setProducts] = useState([]);

  // KB capture dialog state — opened when admin clicks "Save to KB" on any
  // assistant/admin message. Pre-fills body from the message and product from
  // the session.
  const [kbDialogOpen, setKbDialogOpen] = useState(false);
  const [kbForm, setKbForm] = useState({ title: "", body: "", tags: "", product_slug: "" });
  const [kbSaving, setKbSaving] = useState(false);
  const [kbSavedMsgIdx, setKbSavedMsgIdx] = useState(null); // for "Saved!" feedback
  const [kbSourceMsgIdx, setKbSourceMsgIdx] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterProduct !== "all") params.product_slug = filterProduct;
      if (filterSource !== "all") params.source = filterSource;
      const response = await api.get("/support-chat/sessions", { params });
      setSessions(response.data || []);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterProduct, filterSource]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get("/support-products", { params: { active_only: true } });
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Deep-link support: when navigated here with ?session=<id> (e.g. from
  // Dev Backlog's "Source Chat" button), auto-open that session's detail
  // panel so the admin lands directly on the conversation that triggered
  // the backlog item — no manual scroll-and-click through the list.
  const _location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(_location.search);
    const wantSession = params.get("session");
    if (wantSession && (!selectedSession || selectedSession.id !== wantSession)) {
      loadSession(wantSession);
    }
    // intentionally not depending on selectedSession to avoid re-firing
    // after the user navigates away within the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_location.search]);

  const productBySlug = (slug) => products.find((p) => p.slug === slug) || null;

  const loadSession = async (sessionId) => {
    try {
      const response = await api.get(`/support-chat/sessions/${sessionId}`);
      setSelectedSession(response.data);
    } catch (error) {
      toast.error("Failed to load chat");
    }
  };

  const updateStatus = async (sessionId, status) => {
    try {
      await api.put(`/support-chat/sessions/${sessionId}/status?status=${status}`);
      toast.success("Status updated");
      fetchSessions();
      if (selectedSession?.id === sessionId) {
        setSelectedSession(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updatePriority = async (sessionId, priority) => {
    try {
      await api.put(`/support-chat/sessions/${sessionId}/priority?priority=${priority}`);
      toast.success("Priority updated");
      fetchSessions();
      if (selectedSession?.id === sessionId) {
        setSelectedSession(prev => ({ ...prev, priority }));
      }
    } catch (error) {
      toast.error("Failed to update priority");
    }
  };

  const sendAdminReply = async () => {
    if (!replyText.trim() || !selectedSession) return;
    try {
      setSending(true);
      await api.post(`/support-chat/sessions/${selectedSession.id}/admin-reply`, { message: replyText });
      toast.success("Reply sent");
      setReplyText("");
      loadSession(selectedSession.id);
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const openKbDialogFromMessage = (msg, idx) => {
    if (!selectedSession) return;
    // Pre-fill: title is a snippet from the user's question (so it's
    // self-describing in the KB list); body is the curated reply itself.
    const userQuestion = (selectedSession.messages || []).find((m) => m.role === "user");
    const title = (userQuestion?.content || msg.content || "").slice(0, 90).trim();
    setKbForm({
      title,
      body: msg.content || "",
      tags: "",
      product_slug: selectedSession.product_slug || "",
    });
    setKbSourceMsgIdx(idx);
    setKbDialogOpen(true);
  };

  const saveKbArticle = async () => {
    if (!selectedSession) return;
    if (!kbForm.title.trim() || !kbForm.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    try {
      setKbSaving(true);
      const tags = kbForm.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const product_slugs = kbForm.product_slug ? [kbForm.product_slug] : [];
      await api.post(`/support-chat/sessions/${selectedSession.id}/save-to-kb`, {
        title: kbForm.title.trim(),
        body: kbForm.body.trim(),
        product_slugs,
        tags,
        source_message_idx: kbSourceMsgIdx,
      });
      toast.success("Saved to knowledge base");
      setKbSavedMsgIdx(kbSourceMsgIdx);
      setTimeout(() => setKbSavedMsgIdx(null), 2500);
      setKbDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save to KB");
    } finally {
      setKbSaving(false);
    }
  };

  const extractTask = async () => {
    if (!selectedSession) return;
    try {
      setExtracting(true);
      const response = await api.post(`/support-chat/sessions/${selectedSession.id}/extract-task`);
      toast.success(`Task created: ${response.data.title}`);
      setSelectedSession(prev => ({ ...prev, backlog_item_id: response.data.id }));
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract task");
    } finally {
      setExtracting(false);
    }
  };

  // Filter sessions by tab + category + instance + search.
  // (Source/product/category filtering happens server-side via params; the
  // instance dimension is local since the API doesn't index it yet.)
  const tabConfig = TABS.find(t => t.key === activeTab);
  const filteredSessions = sessions.filter(s => {
    if (tabConfig?.statuses && !tabConfig.statuses.includes(s.status || "open")) return false;
    if (filterInstance !== "all" && s.product_instance_slug !== filterInstance) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return s.user_name?.toLowerCase().includes(q) || s.messages?.some(m => m.content?.toLowerCase().includes(q));
    }
    return true;
  });

  // Sort: critical/high first, then by date
  const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 };
  const sortedSessions = [...filteredSessions].sort((a, b) => {
    const pa = priorityOrder[a.priority] ?? 2;
    const pb = priorityOrder[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  const stats = {
    total: sessions.length,
    active: sessions.filter(s => ["open", "escalated", "in_progress"].includes(s.status || "open")).length,
    bugs: sessions.filter(s => s.category === "bug").length,
    critical: sessions.filter(s => s.priority === "critical" || s.priority === "high").length,
  };

  return (
    <div className="space-y-5 p-6" data-testid="support-chats-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Support Chats</h1>
          <p className="text-sm text-zinc-500">Bug reports, feature requests & feedback</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchSessions} variant="outline" size="sm" className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: MessageCircle, color: "blue", label: "Total", value: stats.total },
          { icon: Clock, color: "orange", label: "Active", value: stats.active },
          { icon: Bug, color: "red", label: "Bugs", value: stats.bugs },
          { icon: Flame, color: "red", label: "High Priority", value: stats.critical },
        ].map((s, i) => (
          <Card key={i} className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-${s.color}-500/20 flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 text-${s.color}-400`} />
              </div>
              <div>
                <p className="text-xl font-bold text-zinc-50">{s.value}</p>
                <p className="text-[10px] text-zinc-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs + Filters */}
      <div className="flex items-center gap-4">
        <div className="flex bg-zinc-900 rounded-lg p-0.5 border border-zinc-800">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "px-4 py-1.5 text-sm rounded-md transition-colors",
                activeTab === tab.key ? "bg-zinc-700 text-zinc-50" : "text-zinc-500 hover:text-zinc-300"
              )}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
              {tab.key === "active" && stats.active > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-orange-500/20 text-orange-400">{stats.active}</span>
              )}
            </button>
          ))}
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-32 h-8 text-xs bg-zinc-900 border-zinc-800">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="bug">Bugs</SelectItem>
            <SelectItem value="feature">Features</SelectItem>
            <SelectItem value="question">Questions</SelectItem>
            <SelectItem value="complaint">Complaints</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-32 h-8 text-xs bg-zinc-900 border-zinc-800" data-testid="filter-source">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filterProduct}
          onValueChange={(v) => {
            setFilterProduct(v);
            // Switching product invalidates the instance filter (a Spinhouse
            // instance slug isn't valid under Vexos).
            setFilterInstance("all");
          }}
        >
          <SelectTrigger className="w-44 h-8 text-xs bg-zinc-900 border-zinc-800" data-testid="filter-product">
            <SelectValue placeholder="Product" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => (
              <SelectItem key={p.slug} value={p.slug}>{p.short_label || p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Instance filter — only relevant when a multi-tenant product is
            selected (e.g. Vexos has 13 deployments). Hidden for All Products
            and for products without instances (HUB). */}
        {(() => {
          const sel = products.find((p) => p.slug === filterProduct);
          const liveInstances = (sel?.instances || []).filter((i) => i.is_active !== false);
          if (filterProduct === "all" || liveInstances.length === 0) return null;
          return (
            <Select value={filterInstance} onValueChange={setFilterInstance}>
              <SelectTrigger className="w-44 h-8 text-xs bg-zinc-900 border-zinc-800" data-testid="filter-instance">
                <SelectValue placeholder="Deployment" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                <SelectItem value="all">All {sel.short_label || sel.name} deployments</SelectItem>
                {liveInstances.map((inst) => (
                  <SelectItem key={inst.slug} value={inst.slug}>
                    {inst.label || inst.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        })()}
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-8 text-sm bg-zinc-900 border-zinc-800"
          />
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 500 }}>
        {/* Sessions list */}
        <div className="lg:col-span-2 space-y-1.5 max-h-[650px] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
          ) : sortedSessions.length === 0 ? (
            <p className="text-sm text-zinc-600 text-center py-8">No chats in this tab</p>
          ) : sortedSessions.map(session => {
            const cat = CATEGORY_CONFIG[session.category] || CATEGORY_CONFIG.question;
            const pri = PRIORITY_CONFIG[session.priority] || PRIORITY_CONFIG.normal;
            const sts = STATUS_CONFIG[session.status] || STATUS_CONFIG.open;
            const CatIcon = cat.icon;
            const PriIcon = pri.icon;
            const isSelected = selectedSession?.id === session.id;
            const sProduct = productBySlug(session.product_slug);
            const SProductIcon = sProduct ? (PRODUCT_ICON_MAP[sProduct.icon] || Box) : null;

            return (
              <div
                key={session.id}
                className={cn(
                  "p-3 rounded-lg border cursor-pointer transition-all",
                  isSelected ? "bg-zinc-800/70 border-orange-500/50" : "border-zinc-800/50 hover:bg-zinc-800/30 hover:border-zinc-700"
                )}
                onClick={() => loadSession(session.id)}
                data-testid={`chat-session-${session.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-200 truncate">{session.user_name}</p>
                      <p className="text-[10px] text-zinc-600">{session.user_role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <PriIcon className={cn("w-3 h-3", pri.color.split(" ")[0])} />
                    <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", cat.color)}>
                      <CatIcon className="w-2.5 h-2.5 mr-0.5" />{cat.label}
                    </Badge>
                  </div>
                </div>
                {(sProduct || session.product_instance_label || session.product_instance_mirror_label) && (
                  <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                    {sProduct && (
                      <span className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]",
                        productClass(sProduct.color)
                      )}>
                        {SProductIcon && <SProductIcon className="w-2.5 h-2.5" />}
                        {sProduct.short_label || sProduct.name}
                      </span>
                    )}
                    {session.product_instance_label && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800/60 text-[9px] text-zinc-300">
                        {session.product_instance_label}
                      </span>
                    )}
                    {session.product_instance_mirror_label && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-800 bg-zinc-800/30 text-[9px] text-zinc-400">
                        {session.product_instance_mirror_label}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-1.5 truncate">{session.messages?.[session.messages.length - 1]?.content?.substring(0, 80) || "..."}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", sts.color)}>{sts.label}</Badge>
                  <div className="flex items-center gap-2">
                    {session.backlog_item_id && <Zap className="w-3 h-3 text-green-400" title="Task extracted" />}
                    {session.kb_article_ids?.length > 0 && (
                      <BookOpen className="w-3 h-3 text-emerald-400" title={`${session.kb_article_ids.length} KB article(s)`} />
                    )}
                    <span className="text-[10px] text-zinc-600">{session.messages?.length || 0} msg</span>
                    <span className="text-[10px] text-zinc-600">{new Date(session.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Chat detail */}
        <Card className="lg:col-span-3 bg-zinc-900/50 border-zinc-800 flex flex-col">
          {!selectedSession ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-600">Select a chat</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header controls */}
              <CardHeader className="pb-2 border-b border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-zinc-200">{selectedSession.user_name}</p>
                    <Badge variant="outline" className={cn("text-[9px]", CATEGORY_CONFIG[selectedSession.category]?.color)}>{selectedSession.category}</Badge>
                    {(() => {
                      const sp = productBySlug(selectedSession.product_slug);
                      if (!sp) return null;
                      const Icon = PRODUCT_ICON_MAP[sp.icon] || Box;
                      return (
                        <span className={cn(
                          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]",
                          productClass(sp.color)
                        )}>
                          <Icon className="w-2.5 h-2.5" />
                          {sp.short_label || sp.name}
                        </span>
                      );
                    })()}
                    {selectedSession.product_instance_label && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800/60 text-[9px] text-zinc-300">
                        {selectedSession.product_instance_label}
                      </span>
                    )}
                    {selectedSession.product_instance_mirror_label && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-zinc-800 bg-zinc-800/30 text-[9px] text-zinc-400">
                        {selectedSession.product_instance_mirror_label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={selectedSession.priority || "normal"} onValueChange={(v) => updatePriority(selectedSession.id, v)}>
                      <SelectTrigger className="w-28 h-7 text-xs bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedSession.status || "open"} onValueChange={(v) => updateStatus(selectedSession.id, v)}>
                      <SelectTrigger className="w-28 h-7 text-xs bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="escalated">Escalated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto py-3 space-y-2.5" style={{ maxHeight: 380 }}>
                {selectedSession.messages?.map((msg, idx) => (
                  <div key={idx} className={cn("flex gap-2 group", msg.role === "user" ? "justify-end" : "justify-start")}>
                    {msg.role !== "user" && (
                      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0", msg.role === "admin" ? "bg-purple-600" : "bg-orange-600")}>
                        <Bot className="w-3 h-3 text-zinc-50" />
                      </div>
                    )}
                    <div className={cn("max-w-[80%] px-3 py-2 rounded-lg text-sm relative",
                      msg.role === "user" ? "bg-blue-500/15 text-blue-100" : msg.role === "admin" ? "bg-purple-500/15 text-purple-100" : "bg-zinc-800 text-zinc-300"
                    )}>
                      {msg.role === "admin" && <p className="text-[10px] text-purple-400 mb-0.5">{msg.admin_name}</p>}
                      {/* Strip "[Attached image: /uploads/...]" marker from
                          rendered text. ALSO extract the URL from it as a
                          fallback for legacy messages — image_url field was
                          dropped on save before PR #28, so old sessions only
                          have the marker in content. New ones have both. */}
                      {(() => {
                        const raw = msg.content || "";
                        const markerMatch = raw.match(/\[Attached image:\s*([^\]\s]+)\s*\]/);
                        const cleaned = raw.replace(/\n?\[Attached image:[^\]]*\]/g, "").trim();
                        const imgSrc = msg.image_url || (markerMatch ? markerMatch[1] : null);
                        return (
                          <>
                            {cleaned ? <p className="whitespace-pre-wrap">{cleaned}</p> : null}
                            {imgSrc && (
                              <a
                                href={imgSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block mt-1.5"
                              >
                                <img
                                  src={imgSrc}
                                  alt="Attached"
                                  className="max-h-48 max-w-full rounded border border-zinc-700 hover:border-orange-500 transition-colors"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              </a>
                            )}
                          </>
                        );
                      })()}
                      {msg.role === "user" && msg.current_page && (
                        <p className="text-[10px] text-blue-300/80 mt-1 font-mono">on {msg.current_page}</p>
                      )}
                      <div className="flex items-center justify-between mt-1 gap-2">
                        <p className="text-[10px] text-zinc-600">{new Date(msg.timestamp).toLocaleString()}</p>
                        {msg.role !== "user" && (
                          <button
                            onClick={() => openKbDialogFromMessage(msg, idx)}
                            className={cn(
                              "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors",
                              kbSavedMsgIdx === idx
                                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                : "border-zinc-700 text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200"
                            )}
                            title="Save this answer as a KB article so the AI can reuse it"
                          >
                            <BookOpen className="w-3 h-3" />
                            {kbSavedMsgIdx === idx ? "Saved" : "Save to KB"}
                          </button>
                        )}
                      </div>
                    </div>
                    {msg.role === "user" && (
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <User className="w-3 h-3 text-zinc-50" />
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>

              {/* Actions */}
              <div className="border-t border-zinc-800 p-3 space-y-2">
                {/* Extract task button */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={extractTask}
                    disabled={extracting || !!selectedSession.backlog_item_id}
                    size="sm"
                    className={cn(
                      "flex-1",
                      selectedSession.backlog_item_id
                        ? "bg-green-900/30 text-green-400 border border-green-800 cursor-default"
                        : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    )}
                    data-testid="extract-task-btn"
                  >
                    {extracting ? (
                      <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Extracting...</>
                    ) : selectedSession.backlog_item_id ? (
                      <><CheckCircle2 className="w-4 h-4 mr-1.5" />Task Created</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-1.5" />Extract Task to Backlog</>
                    )}
                  </Button>
                  {selectedSession.backlog_item_id && (
                    <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => window.location.href = "/dev-backlog"}>
                      <ArrowRight className="w-4 h-4 mr-1" />View Backlog
                    </Button>
                  )}
                </div>
                {/* Reply */}
                <div className="flex gap-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Reply as admin..."
                    className="flex-1 bg-zinc-800 border-zinc-700 min-h-[50px] text-sm"
                    onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) sendAdminReply(); }}
                  />
                  <Button onClick={sendAdminReply} disabled={!replyText.trim() || sending} className="bg-purple-600 hover:bg-purple-700 self-end" size="sm">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* "Save to KB" dialog — turns a curated reply into a knowledge-base
          article. Articles are scoped to one or all products; future AI
          replies pull these in as authoritative context. */}
      <Dialog open={kbDialogOpen} onOpenChange={setKbDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100">
              <BookOpen className="w-4 h-4 text-emerald-400" />
              Save to Knowledge Base
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Title</label>
              <Input
                value={kbForm.title}
                onChange={(e) => setKbForm((s) => ({ ...s, title: e.target.value }))}
                placeholder="Short, search-friendly title"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
                data-testid="kb-title"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Body (markdown ok)</label>
              <Textarea
                value={kbForm.body}
                onChange={(e) => setKbForm((s) => ({ ...s, body: e.target.value }))}
                rows={8}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-sm font-mono"
                data-testid="kb-body"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                The AI will see this verbatim when answering similar questions.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Product</label>
                <Select
                  value={kbForm.product_slug || "__all__"}
                  onValueChange={(v) => setKbForm((s) => ({ ...s, product_slug: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="__all__">All products</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.slug}>{p.short_label || p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] text-zinc-500 uppercase">Tags (comma separated)</label>
                <Input
                  value={kbForm.tags}
                  onChange={(e) => setKbForm((s) => ({ ...s, tags: e.target.value }))}
                  placeholder="login, payments, ..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setKbDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={saveKbArticle}
              disabled={kbSaving || !kbForm.title.trim() || !kbForm.body.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="kb-save"
            >
              {kbSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BookOpen className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportChatsPage;
